import { RegionShared } from "@1games-ru/region-shared";
import { InventoryItem, QuickSlot } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useDndMonitor, useDroppable } from "@dnd-kit/core";
import { omit, startsWith } from "lodash";
import { useMemo, useState } from "react";
import clsx from "clsx";
import canPlaceItem from "@/domains/inventory/helpers/canPlaceItem";
import createOccupiedMatrix from "@/domains/inventory/helpers/createOccupiedMatrix";
import getOccupiedCells from "@/domains/inventory/helpers/getOccupiedCells";
import InventoryGridCell from "@/domains/inventory/components/dndkit/InventoryGridCell";
import InventoryGridItem from "@/domains/inventory/components/dndkit/InventoryGridItem";

type Props = {
  inventoryId: number;
  tradeId?: number;
  equipmentId?: number;
  cols: number;
  rows: number;
  items: InventoryItem[];
  onMove?: (
    fromInventoryId: number,
    toInventoryId: number,
    item: InventoryItem,
    oldItem?: InventoryItem,
  ) => Promise<void>;
  onUnEquip?: (equipmentId: number, slotType: string, item?: InventoryItem) => Promise<void>;
  onUnassignQuickSlot?: (item: QuickSlot) => Promise<void>;
  onStack?: (
    fromInventoryId: number,
    toInventoryId: number,
    fromItem: InventoryItem,
    toItem: InventoryItem,
  ) => Promise<void>;
  onAttach?: (
    fromInventoryId: number,
    toInventoryId: number,
    fromItem: InventoryItem,
    toItem: InventoryItem,
  ) => Promise<void>;
  onAddItemToTrade?: (
    fromInventoryId: number,
    toInventoryId: number,
    data: typeof RegionShared.Backend.Requests.TradeAddItemRequest,
  ) => void | Promise<void>;
  onRemoveItemFromTrade?: (
    fromInventoryId: number,
    toInventoryId: number,
    data: typeof RegionShared.Backend.Requests.TradeRemoveItemRequest,
  ) => void | Promise<void>;
  onAddItemToConfiscation?: (fromInventoryId: number, item: InventoryItem) => void | Promise<void>;
  onRemoveItemFromConfiscation?: (
    fromInventoryId: number,
    toInventoryId: number,
    item: InventoryItem,
  ) => Promise<void>;
  onMemoizedItem: (item: InventoryItem | null) => void;
  onDoubleClickItem?: (item: InventoryItem) => void | Promise<void>;
  onPick?: (fromInventoryId: number, item: InventoryItem) => void | Promise<void>;
  onRefresh?: () => Promise<void>;
  onContextMenu?: (e: React.MouseEvent, item: InventoryItem) => void;
  disabled?: boolean;
  disabledItems?: string[];
};

export default function InventoryGrid({
  inventoryId,
  tradeId,
  equipmentId,
  cols,
  rows,
  items,
  onMove,
  onAddItemToConfiscation,
  onRemoveItemFromConfiscation,
  onUnEquip,
  onUnassignQuickSlot,
  onStack,
  onAttach,
  onRefresh,
  onDoubleClickItem,
  onMemoizedItem,
  onAddItemToTrade,
  onRemoveItemFromTrade,
  onPick,
  onContextMenu,
  disabled,
  disabledItems = [],
}: Props) {
  const occupiedMatrix = useMemo(
    () => createOccupiedMatrix(cols, rows, items),
    [cols, rows, items],
  );
  const [dropPosition, setDropPosition] = useState<{ x: number; y: number } | null>(null);
  const [dropCells, setDropCells] = useState<
    | {
        x: number;
        y: number;
      }[]
    | null
  >();
  const [canDrop, setCanDrop] = useState(false);

  const { setNodeRef } = useDroppable({
    id: `grid-inv${inventoryId}-trade${tradeId}-eqv${equipmentId}`,
    disabled,
  });

  useDndMonitor({
    onDragEnd: async (event) => {
      setDropCells(null);
      if (!event.over) return;

      const activeItem = event.active.data.current;
      const overItem = event.over?.data.current;

      const overId = String(event.over.id);

      if (!overId.includes(`inv${inventoryId}`)) {
        return;
      }

      if (overItem && startsWith(overId as string, "cell")) {
        if ((!canDrop && !disabledItems.includes(activeItem.id)) || !activeItem || !overItem)
          return;

        const cellCoord = overItem as { x: number; y: number };
        try {
          const dropItem = omit(activeItem, ["payload"]) as InventoryItem;
          const { payload } = activeItem;

          if (payload?.slot && payload?.equipmentId) {
            await onUnEquip?.(payload.equipmentId, payload.slot, {
              ...dropItem,
              position: { ...cellCoord },
            });
          } else if (payload?.quickSlot) {
            await onUnassignQuickSlot?.({
              id: dropItem.itemId,
              inventoryItem: dropItem,
              slotNumber: payload.quickSlot,
            });
          } else if (startsWith(event.active.id as string, "listItem")) {
            await onPick?.(dropItem.inventoryId, dropItem);
          } else {
            await onMove?.(
              dropItem.inventoryId,
              inventoryId,
              { ...dropItem, position: { ...cellCoord } },
              dropItem,
            );

            // TRADE
            await onAddItemToTrade?.(dropItem.inventoryId, inventoryId, {
              inventoryItemId: dropItem.id,
              tradeId,
            });

            await onRemoveItemFromTrade?.(dropItem.inventoryId, inventoryId, {
              inventoryItemId: dropItem.id,
              tradeId,
            });

            // CONFISCATION
            await onAddItemToConfiscation?.(dropItem.inventoryId, {
              ...dropItem,
              position: { ...cellCoord },
            });

            await onRemoveItemFromConfiscation?.(dropItem.inventoryId, inventoryId, dropItem);
          }
        } catch (e) {
          console.log(e);
        } finally {
          await onRefresh?.();
        }
      }

      if (overItem && startsWith(overId as string, "item")) {
        try {
          if (activeItem.itemTemplate.isAttachable) {
            await onAttach?.(
              activeItem.inventoryId,
              overItem.inventoryId,
              activeItem as InventoryItem,
              overItem as InventoryItem,
            );
          } else {
            throw new Error("");
          }
        } catch (e) {
          if (activeItem.itemTemplate.isStackable && overItem.itemTemplate.isStackable) {
            await onStack?.(
              activeItem.inventoryId,
              overItem.inventoryId,
              activeItem as InventoryItem,
              overItem as InventoryItem,
            );
          }
          console.log(e);
        } finally {
          await onRefresh?.();
        }
      }
    },
    onDragMove(event) {
      if (!event.over) return;

      const { active } = event;

      const overId = String(event.over.id);

      // Обрабатываем только если дроп был в ЭТУ сетку
      if (!overId.includes(`inv${inventoryId}`)) {
        setDropCells(null);
        setCanDrop(false);
        setDropPosition(null);
        return;
      }

      if (dropPosition && active?.data?.current) {
        setCanDrop(
          !disabled && dropPosition && active.data.current
            ? canPlaceItem(
                cols,
                rows,
                occupiedMatrix,
                active.data.current.size,
                dropPosition,
                active.data.current.position,
              )
            : false,
        );
        setDropCells(getOccupiedCells(dropPosition.x, dropPosition.y, active.data.current.size));
      } else {
        setDropCells(null);
        setCanDrop(false);
      }

      if (!event.collisions.some((item) => startsWith(item.id as string, "grid"))) {
        setDropCells(null);
        setDropPosition(null);
      }
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "relative grid rounded-[0.625rem] overflow-clip",
        "border border-[#1A1A1A] border-collapse",
        "grid-container",
        { "pointer-events-none": disabled },
      )}
      style={{
        gridTemplateColumns: `repeat(${cols}, 4.375rem)`,
        width: `calc(4.375rem * ${cols} + 1px)`,
        height: `calc(4.375rem * ${rows} + 1px)`,
      }}
    >
      {items.map((gridItem) => {
        const isDisabled = disabledItems.includes(gridItem.id);
        return (
          <InventoryGridItem
            inventoryId={inventoryId}
            tradeId={tradeId}
            key={gridItem.id}
            onContextMenu={onContextMenu}
            onMemoizedItem={onMemoizedItem}
            onDoubleClickItem={onDoubleClickItem}
            item={gridItem}
            disabled={isDisabled}
          />
        );
      })}
      {occupiedMatrix.map((row, j) =>
        row.map((_, i) => {
          const isSelect = dropCells?.some((cell) => cell.x === i && cell.y === j);
          return (
            <InventoryGridCell
              inventoryId={inventoryId}
              tradeId={tradeId}
              key={`${-i}${-j}`}
              x={i}
              y={j}
              onHover={setDropPosition}
              isAvailable={canDrop}
              isSelect={isSelect}
              disabled={disabled}
            />
          );
        }),
      )}
    </div>
  );
}
